import { FunctionComponent, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import useNonInitialEffect from '@versiondos/hooks';
import Spinner from 'components/UI/Spinner';
import WalkingIsEmpty from './List/WalkingIsEmpty';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import { SelectionBar } from 'components/UI/V3/SelectionBar';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { WalkingForm } from './Form/WalkingForm';
import WalkingServicesTable from './List/WalkingServicesTable';
import { Service, isWalkingService } from 'model/Service';
import { useAuth, useWalkingServices, DEFAULT_PAGE_SIZE, DEFAULT_PAGE } from 'hooks';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { RootState } from 'store';
import styles from './WalkingComponent.module.scss';
import { resetServicesThunk } from 'actions/services/ServicesActions';
import Button from 'components/UI/V3/Button/Button';
import OrganizationMarketplacesFilter from 'components/Bookings2/List/OrganizationMarketplacesFilter';

const WalkingComponent: FunctionComponent = () => {
    const {
        services: walkingServices,
        savedService,
        meta,
        fetchWalkingServices,
        duplicateWalkingService,
        toggleWalkingServiceStatus
    } = useWalkingServices();

    const dispatch = useDispatch();
    const auth = useAuth();

    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);

    const [loading, setLoading] = useState({ services: false, initialLoading: true });
    const [selectedOption, setSelectedOption] = useState('active');
    const [searchText, setSearchText] = useState('');
    const [marketplaceIds, setMarketplaceIds] = useState<Array<number>>(() =>
        auth.marketplaceId ? [auth.marketplaceId] : []
    );
    const [pageInfo, setPageInfo] = useState({
        page: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE
    });
    const [showAddWalking, setShowAddWalking] = useState(false);
    const [editingService, setEditingService] = useState<Service | null>(null);
    const [lastSavedServiceId, setLastSavedServiceId] = useState<number | undefined>();
    const [isEditingExisting, setIsEditingExisting] = useState(false);
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    const fetchServices = () => {
        setLoading(prev => ({ ...prev, services: true }));
        fetchWalkingServices(pageInfo.page, pageInfo.pageSize, selectedOption, searchText, marketplaceIds);
    };

    useEffect(() => {
        dispatch(resetServicesThunk());
        fetchServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!loading.initialLoading) {
            fetchServices();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageInfo, selectedOption, searchText, marketplaceIds]);

    useNonInitialEffect(() => {
        switch (servicesStatus) {
            case ServicesStatus.FetchSuccess:
            case ServicesStatus.Error:
                setLoading(prev => ({ services: false, initialLoading: false }));
                break;
            case ServicesStatus.SaveBulkSuccess:
                fetchServices();
                break;
        }
    }, [servicesStatus]);

    useEffect(() => {
        if (savedService && isWalkingService(savedService)) {
            if (savedService.id && (savedService.id !== lastSavedServiceId || isEditingExisting)) {
                setLastSavedServiceId(savedService.id);
                setIsEditingExisting(false);
                fetchServices();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedService]);

    const handlePageChange = (page: number) => {
        setLoading(prev => ({ ...prev, services: true }));
        setPageInfo(prev => ({ ...prev, page }));
    };

    const handlePageSizeChange = (size: number) => {
        setLoading(prev => ({ ...prev, services: true }));
        setPageInfo({ page: 1, pageSize: size });
    };

    const handleStatusChange = (option: string) => {
        setLoading(prev => ({ ...prev, services: true }));
        setSelectedOption(option);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    };

    const handleSearchChange = (text: string) => {
        setLoading(prev => ({ ...prev, services: true }));
        setSearchText(text);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    };

    const handleMarketplaceIdsChange = (ids: Array<number>) => {
        setLoading(prev => ({ ...prev, services: true }));
        setMarketplaceIds(ids);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    };

    const handleEditService = (service: Service) => {
        setEditingService(service);
        setIsEditingExisting(true);
        setShowAddWalking(true);
    };

    const handleDuplicateService = (service: Service) => {
        setIsEditingExisting(false);
        duplicateWalkingService(service);
    };

    const handleDeactivateService = (service: Service) => {
        toggleWalkingServiceStatus(service, {
            page: pageInfo.page,
            per: pageInfo.pageSize,
            status: selectedOption,
            searchText: searchText,
            marketplaceIds
        });
    };

    const handleCloseModal = () => {
        setShowAddWalking(false);
        setEditingService(null);
        setIsEditingExisting(false);
    };

    const headerRightContent = (
        <div className={styles.headerRightContent}>
            <OrganizationMarketplacesFilter
                selected={marketplaceIds}
                singleSelection
                onChangeSelection={handleMarketplaceIdsChange}
                className={styles.locationsFilter}
            />
            <Button
                leftIcon={faPlus}
                label={isMobile ? 'Add' : 'Add Service'}
                onClick={() => setShowAddWalking(true)}
                id="walking_add_button"
            />
        </div>
    );

    const renderContent = () => {
        if (loading.initialLoading) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            );
        }

        if (
            walkingServices.length === 0 &&
            meta?.totalServices === 0 &&
            selectedOption === 'active' &&
            !searchText &&
            !loading.services
        ) {
            return (
                <div className={styles.root}>
                    <WalkingIsEmpty
                        onClick={() => setShowAddWalking(true)}
                        headerRightContent={headerRightContent}
                    />
                </div>
            );
        }

        return (
            <div className={styles.root}>
                <div className={styles.headWrapper}>
                    <DefaultHeader
                        title={'Walking Services'}
                        buttonLabel={isMobile ? 'Add' : 'Add Service'}
                        displayButton={true}
                        onClick={() => setShowAddWalking(true)}
                        id="walking_add_button"
                        className={styles.headerContainer}
                        customButton={headerRightContent}
                    />

                    <SelectionBar
                        options={[
                            { id: 'active', name: 'Active' },
                            { id: 'inactive', name: 'Inactive' },
                            { id: 'all', name: 'All' }
                        ]}
                        selectedId={selectedOption}
                        onSelect={handleStatusChange}
                        className={styles.selectionBar}
                    />

                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={handleSearchChange}
                        placeholder="Search by service name"
                        className={styles.searchBarContainer}
                    />
                </div>

                <div className={styles.tableContainer}>
                    <WalkingServicesTable
                        services={walkingServices}
                        meta={meta}
                        isLoading={loading.services}
                        isEmpty={walkingServices.length === 0 && !loading.services}
                        onEdit={handleEditService}
                        onDuplicate={handleDuplicateService}
                        onDeactivate={handleDeactivateService}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onRetry={() => fetchServices()}
                    />
                </div>
            </div>
        );
    };

    return (
        <>
            {renderContent()}
            {showAddWalking && (
                <WalkingForm
                    mode={editingService ? 'edit' : 'add'}
                    service={editingService || undefined}
                    onClose={handleCloseModal}
                />
            )}
        </>
    );
};

export default WalkingComponent;
